import { useCallback, useEffect, type FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { Button } from '@trello/nachos/button';

import { usePersonalProductivityCohortValue } from 'app/src/components/PersonalProductivityBeta/usePersonalProductivityCohortValue';

import styles from './FreeSelectionInformationModal.less';

interface FreeSelectionInformationModalProps
  extends FreeSelectionInformationModalWrapperProps {
  cohort: string | undefined;
}
export const FreeSelectionInformationModal: FunctionComponent<
  FreeSelectionInformationModalProps
> = ({ onBackToPlans, onContinue, onComparePlans, cohort }) => {
  useEffect(() => {
    Analytics.sendScreenEvent({
      name: 'endOfTrialFreeSelectionInformationModal',
    });
  });

  const onBackToPlansClick = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'backToPlansButton',
      source: 'endOfTrialFreeSelectionInformationModal',
    });
    onBackToPlans();
  }, [onBackToPlans]);

  const onContinueClick = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'continueWithFreeButton',
      source: 'endOfTrialFreeSelectionInformationModal',
    });
    onContinue();
  }, [onContinue]);

  const onComparePlansClick = useCallback(() => {
    Analytics.sendClickedLinkEvent({
      linkName: 'compareAllPlans',
      source: 'endOfTrialFreeSelectionInformationModal',
    });
    onComparePlans();
  }, [onComparePlans]);

  const personalProductivityCohort = cohort === 'ga';

  return (
    <div className={styles.freeSelectionInformationModal}>
      <div className={styles.modalHeader}>
        <h2 className={styles.modalTitle}>
          <FormattedMessage
            id="templates.end_of_trial_friction.free-selection-modal-title"
            description="Title for the free selection modal which describes the features and limits of Trello free"
            defaultMessage="Things to know about Trello free"
          />
        </h2>
        <p>
          <FormattedMessage
            id="templates.end_of_trial_friction.free-selection-modal-description"
            description="Description for the free selection modal which describes the features and limits of Trello free"
            defaultMessage="The free version of Trello has limited features. You can upgrade at anytime."
          />
        </p>
      </div>
      <div className={styles.freeLimitsAndUnavailableFeatures}>
        <div>
          <p className={styles.subheading}>
            <FormattedMessage
              id="templates.end_of_trial_friction.free-selection-modal-workspace-limits"
              description="Subheading for the free selection modal which describes the features and limits of Trello free"
              defaultMessage="Free Workspace limits"
            />
          </p>
          <ul>
            <li>
              <FormattedMessage
                id="templates.end_of_trial_friction.free-selection-modal-workspace-limits-boards"
                description="Trello free has a 10 board limit"
                defaultMessage="Up to 10 boards"
              />
            </li>
            {personalProductivityCohort ? (
              <li>
                <FormattedMessage
                  id="templates.end_of_trial_friction.free-selection-modal-view-only-planner"
                  description="Trello free has view-only Planner"
                  defaultMessage="View-only Planner"
                />
              </li>
            ) : (
              <li>
                <FormattedMessage
                  id="templates.end_of_trial_friction.free-selection-modal-workspace-limits-collaborators"
                  description="Trello free has a 10 collaborator limit"
                  defaultMessage="Up to 10 collaborators"
                />
              </li>
            )}
            <li>
              <FormattedMessage
                id="templates.end_of_trial_friction.free-selection-modal-workspace-limits-automation"
                description="Trello free has a 250 automation command runs per month limit"
                defaultMessage="250 automation command runs per month"
              />
            </li>
          </ul>
        </div>
        <div>
          <p className={styles.subheading}>
            <FormattedMessage
              id="templates.end_of_trial_friction.free-selection-modal-unavailable-features"
              description="Subheading for the free selection modal which describes the features and limits of Trello free"
              defaultMessage="Unavailable features"
            />
          </p>
          <ul>
            {personalProductivityCohort ? (
              <>
                <li>
                  <FormattedMessage
                    id="templates.end_of_trial_friction.free-selection-modal-unavailable-features-atlassian-intelligence"
                    description="Trello free does not have Atlassian Intelligence (AI)"
                    defaultMessage="Atlassian Intelligence (AI)"
                  />
                </li>
                <li>
                  <FormattedMessage
                    id="templates.end_of_trial_friction.free-selection-modal-unavailable-features-scheduling-to-dos"
                    description="Trello free does not have scheduling to-dos in Planner"
                    defaultMessage="Scheduling to-dos in Planner"
                  />
                </li>
                <li>
                  <FormattedMessage
                    id="templates.end_of_trial_friction.free-selection-modal-unavailable-features-card-mirroring"
                    description="Trello free does not have card mirroring"
                    defaultMessage="Card mirroring"
                  />
                </li>
              </>
            ) : (
              <>
                <li>
                  <FormattedMessage
                    id="templates.end_of_trial_friction.free-selection-modal-unavailable-features-views"
                    description="Trello free does not have views like Table, Timeline, and Dashboard"
                    defaultMessage="Views like Table, Timeline, and Dashboard"
                  />
                </li>
              </>
            )}
            <li>
              <FormattedMessage
                id="templates.end_of_trial_friction.free-selection-modal-unavailable-features-custom-fields"
                description="Trello free does not have custom fields"
                defaultMessage="Custom Fields"
              />
            </li>
            {!personalProductivityCohort && (
              <li>
                <FormattedMessage
                  id="templates.end_of_trial_friction.free-selection-modal-unavailable-features-checklist"
                  description="Trello free does not have checklist assignees and due dates"
                  defaultMessage="Checklist assignees and due dates"
                />
              </li>
            )}
            <li>
              <FormattedMessage
                id="templates.end_of_trial_friction.free-selection-modal-unavailable-features-lists"
                description="Trello free does not have collapsible lists, list colors, and more"
                defaultMessage="Collapsible lists, list colors, and more"
              />
            </li>
          </ul>
        </div>
      </div>
      <Button appearance="link" onClick={onComparePlansClick}>
        <FormattedMessage
          id="templates.end_of_trial_friction.free-selection-modal-compare-all-plans"
          description="Button to open compare all plans modal"
          defaultMessage="Compare all plans"
        />
      </Button>
      <div className={styles.modalFooter}>
        <Button appearance="default" onClick={onBackToPlansClick}>
          <FormattedMessage
            id="templates.end_of_trial_friction.back-to-plans"
            description="Button to go back to plan selection modal"
            defaultMessage="Back to plans"
          />
        </Button>
        <Button appearance="primary" onClick={onContinueClick}>
          <FormattedMessage
            id="templates.end_of_trial_friction.continue-with-free"
            description="Button to continue with free plan"
            defaultMessage="Continue with free"
          />
        </Button>
      </div>
    </div>
  );
};

interface FreeSelectionInformationModalWrapperProps {
  onBackToPlans: () => void;
  onContinue: () => void;
  onComparePlans: () => void;
}

export const FreeSelectionInformationModalWrapper: FunctionComponent<
  FreeSelectionInformationModalWrapperProps
> = ({ onBackToPlans, onContinue, onComparePlans }) => {
  const cohort = usePersonalProductivityCohortValue();

  return (
    <FreeSelectionInformationModal
      onBackToPlans={onBackToPlans}
      onContinue={onContinue}
      onComparePlans={onComparePlans}
      cohort={cohort}
    />
  );
};
